import { pick } from 'lodash';

import type { IUser } from '@wix/yoshi-flow-editor';
import {
  followMember,
  unfollowMember,
} from '@wix/ambassador-members-v3-follow/http';
import { getMyMember } from '@wix/ambassador-members-v1-member/http';
import { Set as WixMembersFieldSet } from '@wix/ambassador-members-v1-member/types';
import { getSettings } from '@wix/ambassador-social-groups-v2-groups-app-settings/http';

import { mapMember } from 'api/members/selectors';

import { createAsyncThunk, getCurrentUser } from '../utils';
import type { IApplicationUser } from './types';

export const fetchSettings = createAsyncThunk(
  'application:fetchSettings',
  async (_: void, thunkAPI) => {
    const response = await thunkAPI.extra.httpClient.request(getSettings({}));

    return response.data.settings;
  },
);

type FollowParams = {
  memberId: string;
  feedItemId?: string;
};

export const login = createAsyncThunk(
  'application:login',
  async (_, thunkAPI) => {
    const { controllerConfig } = thunkAPI.extra;

    try {
      await controllerConfig.wixCodeApi.user.promptLogin({ modal: true });

      return thunkAPI.dispatch(fetchCurrentUserProfile());
    } catch (err) {
      return thunkAPI.rejectWithValue(false);
    }
  },
  { hideErrorMessage: true },
);

export const fetchCurrentUserProfile = createAsyncThunk<
  Partial<IApplicationUser>,
  void,
  { pendingMeta: IUser }
>(
  'application:currentUserProfile',
  async function (_, thunkAPI) {
    const flowApi = thunkAPI.extra;

    const instance = flowApi.controllerConfig.wixCodeApi.site.getAppToken?.(
      flowApi.environment.appDefinitionId,
    );

    const wixUser = getCurrentUser(
      flowApi.controllerConfig.wixCodeApi.user.currentUser,
      instance,
    );

    const user = pick(wixUser, 'loggedIn', 'role', 'instance');

    if (!wixUser.loggedIn) {
      return {
        isSiteAdmin: false,
        siteMemberId: wixUser.id,
        ...user,
      };
    }

    const [roles, response] = await Promise.all([
      flowApi.controllerConfig.wixCodeApi.user.currentUser
        .getRoles()
        .catch(() => []),
      flowApi.httpClient.request(
        getMyMember({
          fieldsets: [WixMembersFieldSet.FULL],
        }),
      ),
    ]);

    if (!response.data.member) {
      throw new Error('Current user profile not found');
    }

    return {
      ...user,
      ...mapMember(response.data.member),
      isSiteAdmin:
        user.role === 'Admin' || roles.some((role) => role.name === 'Admin'),
    } as IApplicationUser;
  },
  {
    getPendingMeta(base, thunkAPI) {
      const flowApi = thunkAPI.extra;

      const instance = flowApi.controllerConfig.wixCodeApi.site.getAppToken?.(
        flowApi.environment.appDefinitionId,
      );

      return getCurrentUser(
        flowApi.controllerConfig.wixCodeApi.user.currentUser,
        instance,
      );
    },
  },
);
